import { useState, useEffect } from "react";
import { C, S } from "../../utils/theme";
import { Modal, Txtarea, Inp, Badge, PageHdr, Empty, StatCard, LevelBadge } from "../../components/UI";
import { commAPI } from "../../utils/api";

export default function AdminQA() {
  const [threads, setThreads] = useState([]);
  const [hasKey, setHasKey] = useState(false);
  const [replyModal, setReplyModal] = useState(null);
  const [reply, setReply] = useState("");
  const [keyModal, setKeyModal] = useState(false);
  const [tempKey, setTempKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [aiLoading, setAiLoading] = useState(null);
  const [aiError, setAiError] = useState("");

  const load = async () => {
    try {
      const [q, k] = await Promise.all([commAPI.getQA(), commAPI.aiKeyExists()]);
      setThreads(q.data || []);
      setHasKey(!!k.hasKey);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const unanswered = threads.filter(q => !q.answer);
  const answered = threads.filter(q => q.answer);

  const submitReply = async () => {
    if (!reply.trim()) return;
    try {
      await commAPI.answerQA(replyModal.id, reply);
      setReplyModal(null); setReply(""); load();
    } catch (e) { alert(e.message); }
  };

  const triggerAI = async (thread) => {
    setAiLoading(thread.id); setAiError("");
    try {
      await commAPI.askAI(thread.id);
      load();
    } catch (e) { setAiError(e.message); }
    finally { setAiLoading(null); }
  };

  const delThread = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try { await commAPI.deleteQA(id); load(); } catch (e) { alert(e.message); }
  };

  const saveKey = async () => {
    if (!tempKey.trim()) return;
    setSavingKey(true);
    try {
      await commAPI.saveSetting("anthropic_api_key", tempKey.trim());
      setTempKey(""); setKeyModal(false); load();
    } catch (e) { alert(e.message); }
    finally { setSavingKey(false); }
  };

  return (
    <div>
      <PageHdr title="Q&A Management" sub={`${unanswered.length} awaiting reply`}
        action={
          <button style={S.btn(hasKey ? "success" : "primary")} onClick={() => { setTempKey(""); setKeyModal(true); }}>
            🤖 {hasKey ? "AI Key Set ✓" : "Set Claude AI Key"}
          </button>
        }
      />
      <div style={{ ...S.alert("info"), marginBottom: 16 }}>
        Yeh key ek hi baar set hoti hai aur poori university ke <strong>sab students</strong> ke liye automatically kaam karti hai — har student ko alag se set karne ki zaroorat nahi.
      </div>
      {aiError && <div style={{ ...S.alert("danger"), marginBottom: 16 }}>⚠️ {aiError}</div>}
      <div style={S.statsGrid}>
        <StatCard icon="⏳" label="Unanswered" value={unanswered.length} color={C.goldDark} />
        <StatCard icon="✅" label="Answered" value={answered.length} color="#10B981" />
        <StatCard icon="🤖" label="AI Answered" value={threads.filter(q => q.ai_answered).length} color={C.navy} />
        <StatCard icon="💬" label="Total" value={threads.length} color="#8B5CF6" />
      </div>
      {unanswered.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.goldDark, marginBottom: 14 }}>⏳ Unanswered ({unanswered.length})</h3>
          {unanswered.map(q => (
            <div key={q.id} style={{ ...S.card, marginBottom: 12, borderLeft: `3px solid ${C.gold}` }}>
              <div style={{ padding: "17px 22px", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>"{q.question}"</div>
                    {q.course_level && <LevelBadge level={q.course_level} />}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>
                    By <strong style={{ color: C.textMid }}>{q.student_name}</strong> • {q.course_name} • {q.created_at ? new Date(q.created_at).toLocaleDateString("en-PK") : ""}
                  </div>
                  {aiLoading === q.id && <div style={{ marginTop: 10, fontSize: 13, color: C.navy }}>🤖 AI is thinking...</div>}
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  {hasKey && <button style={S.btn("primary", "sm")} onClick={() => triggerAI(q)} disabled={aiLoading === q.id}>🤖 AI Reply</button>}
                  <button style={S.btn("success", "sm")} onClick={() => { setReplyModal(q); setReply(""); }}>✍️ Manual</button>
                  <button style={S.btn("danger", "sm")} onClick={() => delThread(q.id)}>🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#059669", marginBottom: 14 }}>✅ Answered ({answered.length})</h3>
        {answered.length === 0 ? <Empty icon="💬" text="No answered questions yet" />
          : answered.map(q => (
            <div key={q.id} style={{ ...S.card, marginBottom: 12, borderLeft: `3px solid ${q.ai_answered ? C.navy : "#10B981"}` }}>
              <div style={{ padding: "17px 22px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: C.text, marginBottom: 4 }}>Q: {q.question}</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>By {q.student_name} • {q.course_name}</div>
                    <div style={{ padding: "10px 14px", background: q.ai_answered ? C.navyPale : "#ECFDF5", borderRadius: 8, fontSize: 13, color: C.textMid, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      <span style={{ fontSize: 11, color: q.ai_answered ? C.navy : "#059669", fontWeight: 700, display: "block", marginBottom: 6 }}>{q.ai_answered ? "🤖 AI Answer" : "✅ Manual Reply"}</span>
                      {q.answer}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 12, flexShrink: 0 }}>
                    <button style={S.btn("glass", "sm")} onClick={() => { setReplyModal(q); setReply(q.answer || ""); }}>✏️</button>
                    <button style={S.btn("danger", "sm")} onClick={() => delThread(q.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            </div>
          ))
        }
      </div>

      <Modal open={!!replyModal} onClose={() => setReplyModal(null)} title="Reply to Question"
        footer={<><button style={S.btn("ghost")} onClick={() => setReplyModal(null)}>Cancel</button><button style={S.btn("primary")} onClick={submitReply}>Save Reply</button></>}>
        {replyModal && <div style={S.alert("info")}><strong>Q:</strong> {replyModal.question}</div>}
        <Txtarea label="Your Answer" rows={5} value={reply} onChange={e => setReply(e.target.value)} />
      </Modal>

      <Modal open={keyModal} onClose={() => setKeyModal(false)} title="🤖 Claude AI API Key"
        footer={<>
          <button style={S.btn("ghost")} onClick={() => setKeyModal(false)}>Cancel</button>
          <button style={S.btn("primary")} onClick={saveKey} disabled={!tempKey.trim() || savingKey}>{savingKey ? "Saving..." : "Save Key"}</button>
        </>}>
        <div style={S.alert("info")}>
          Yeh key server pe ek hi jagah save hoti hai — poore university ke sab students ka "Get AI Answer" automatically isi key se kaam karega.
        </div>
        <Inp label="Anthropic API Key" type="password" placeholder="sk-ant-api03-..." value={tempKey} onChange={e => setTempKey(e.target.value)} />
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: -8 }}>
          Get key from <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" style={{ color: C.navy }}>console.anthropic.com</a>
        </div>
      </Modal>
    </div>
  );
}
